import { ReconLabClient } from '../client';
import { OsintSearchResponse, ApiResponse } from '../types';

/**
 * OSINT search module
 */
export class OsintModule {
  private client: ReconLabClient;

  /**
   * Create a new OSINT module
   * @param client The API client
   */
  constructor(client: ReconLabClient) {
    this.client = client;
  }

  /**
   * Perform an OSINT search using API key authentication
   * @param query The search query
   * @returns Promise resolving to the search results
   */
  async search(query: string): Promise<OsintSearchResponse> {
    if (!this.client.getApiKey()) {
      throw new Error('API key is required for OSINT search. Set it using client.setApiKey()');
    }

    const response = await this.client.post<OsintSearchResponse>('/api/v1/search', { query });
    
    if (!response.success || !response.data) {
      throw new Error(response.error || 'Failed to perform OSINT search');
    }
    
    return response.data;
  }

  /**
   * Perform an OSINT search with wallet address (no API key required)
   * @param query The search query
   * @param walletAddress The wallet address
   * @returns Promise resolving to the search results
   */
  async searchWithWallet(query: string, walletAddress: string): Promise<OsintSearchResponse> {
    const response = await this.client.post<OsintSearchResponse>('/api/osint', { 
      query, 
      walletAddress 
    });
    
    if (!response.success || !response.data) {
      throw new Error(response.error || 'Failed to perform OSINT search');
    }
    
    return response.data;
  }

  /**
   * Get search results by category
   * @param query The search query
   * @param category The category to filter by
   * @returns Promise resolving to filtered search results
   */
  async searchByCategory(query: string, category: string): Promise<OsintSearchResponse> {
    const results = await this.search(query);
    
    // Filter results by category
    const filteredDatabases = results.databases.filter(db => 
      db.category.toLowerCase() === category.toLowerCase()
    );
    
    // Update the results with filtered databases
    const filteredResults: OsintSearchResponse = {
      ...results,
      databases: filteredDatabases,
      summary: {
        ...results.summary,
        totalDatabases: filteredDatabases.length,
        totalResults: filteredDatabases.reduce((sum, db) => sum + db.resultCount, 0),
      },
    };
    
    return filteredResults;
  }

  /**
   * Get search results with specific field types
   * @param query The search query
   * @param fieldTypes The field types to include
   * @returns Promise resolving to filtered search results
   */
  async searchByFieldTypes(query: string, fieldTypes: string[]): Promise<OsintSearchResponse> {
    const results = await this.search(query);
    
    // Filter entries by field types
    const filteredDatabases = results.databases.map(db => ({
      ...db,
      entries: db.entries.map(entry => ({
        ...entry,
        fields: entry.fields.filter(field => 
          fieldTypes.includes(field.type)
        ),
      })).filter(entry => entry.fields.length > 0),
    })).filter(db => db.entries.length > 0);
    
    // Update the results with filtered databases
    const filteredResults: OsintSearchResponse = {
      ...results,
      databases: filteredDatabases,
      summary: {
        ...results.summary,
        totalDatabases: filteredDatabases.length,
        totalResults: filteredDatabases.reduce((sum, db) => sum + db.resultCount, 0),
      },
    };
    
    return filteredResults;
  }

  /**
   * Get all available categories from search results
   * @param query The search query
   * @returns Promise resolving to an array of categories
   */
  async getCategories(query: string): Promise<string[]> {
    const results = await this.search(query);
    return results.summary.categories;
  }
}
